// 전직 퀘스트(3개월 C10-5, PRD 3A.1 Lv30 전직 I·Lv40 전직 II — 정본 docs/SKILL-TREE.md 13절). 순수 데이터·판정(DOM·저장소 없음).
//
// 흐름: 마을 교관(세르)에게 말을 건다 → 그 단계의 관문 3개(연격·인내·교관의 시험)를 차례로 넘는다 → 교관 앞에서 갈래를 고른다(I) 또는 승급한다(II)
// → 그 갈래의 전직 스킬(카엘 9·10번, 라이라 12·13번 — JOB_SKILL_ID)을 배운다. 관문 판은 용사의 시련(trialRun)과 같은 '경험치 없는 조건 판'이다(같은 판 흐름·문턱 실패·고정 id 지급).
// 던파의 전직 퀘스트(짧은 전용 던전 몇 개 → 전직관에게 갈래 선택)를 따랐다. 관문을 셋으로 둔 이유: 전직은 '손이 익었다'는 증명이어야 해서
// 연계(연격)·버티기(인내)·보스(완벽한 승리)를 각각 한 번씩 확인한다.
//
// 저장: 새 저장 키를 만들지 않는다. 관문 통과·갈래 선택·승급은 모두 save.v1 고정 id 보너스 영수증이다(시련과 같은 규칙, 접혀도 해시로 남는다).
//  - 관문 통과 = `jobgate:<관문 id>`(골드 영수증, 강화석은 장비 장부 `jobgate:<id>:s`) · 갈래 선택 = `job:<영웅>:1:a|b` · 전직 II 승급 = `job:<영웅>:2`
//  두 탭이 서로 다른 갈래를 동시에 고르면 두 영수증이 다 남을 수 있다: 이때는 a가 이긴다(jobState가 a를 먼저 본다 — 결정적이고 되돌릴 수 없는 쪽).
// C10-6: 라이라(궁수) 추가(docs/SKILL-TREE.md 14절). 관문 스테이지·조건은 카엘과 같고 이름·교관 대사만 궁수에 맞춘다(관문 id job-lyra-<단계>-<n>).
// 교관은 마을마다 한 명뿐인 세르다(VILLAGE_NPCS의 trainer, 궁수 전용 NPC 없음) — 활을 쥔 제자에게 하는 말로 바꿨다.
import type { AnyStageId } from './types';
import { DIFFICULTY_ILVL_ADD, stoneTierForIlvl } from './economy';
import { REGIONS_90D } from './world';
import type { RegionNo } from './types';
import type { TrialKind, TrialReward } from './trials';

export type JobTier = 1 | 2;
export type JobBranch = 0 | 1;
/** 전직 퀘스트가 있는 영웅 */
export type JobHeroId = 'kael' | 'lyra';
/** 순서가 관문 이벤트 번호(JOB_EVENT_BASE + JOB_GATES 인덱스)를 정한다: 새 영웅은 뒤에만 붙인다 */
export const JOB_HEROES: readonly JobHeroId[] = ['kael', 'lyra'];
export const isJobHero = (h: unknown): h is JobHeroId => h === 'kael' || h === 'lyra';

/** 단계별 필요 레벨(PRD 3A.1) */
export const JOB_LEVEL: Readonly<Record<JobTier, number>> = { 1: 30, 2: 40 };

/** 갈래(content/heroes.ts jobs와 같은 이름)와 갈래 소개 */
export interface JobBranchDef { readonly name: string; readonly motto: string; readonly color: string; readonly skills: readonly [string, string] }
export const JOB_BRANCHES: Readonly<Record<JobHeroId, readonly [JobBranchDef, JobBranchDef]>> = {
  kael: [
    { name: '블러드 버서커', motto: '피를 대가로 멈추지 않는 칼날. 슈퍼아머로 무리 한가운데를 버틴다.', color: '#e0313c', skills: ['피의 폭주', '흡혈 참격'] },
    { name: '소드 마스터', motto: '검기를 날려 먼 곳부터 벤다. 일직선 무리와 도망치는 적에 강하다.', color: '#8fd8ff', skills: ['검기 방출', '천검 낙하'] },
  ],
  lyra: [
    { name: '윈드 스나이퍼', motto: '숨을 멈추고 한 발에 꿰뚫는다. 먼 거리 관통 저격으로 일직선 무리와 보스를 노린다.', color: '#7ff0ff', skills: ['차지샷', '관통 폭풍'] },
    { name: '트랩 레인저', motto: '길목에 덫을 깔고 기다린다. 속박한 무리를 연쇄 폭발로 한꺼번에 날린다.', color: '#b8e05a', skills: ['지뢰밭', '덫 연쇄'] },
  ],
};
/** 영웅 이름·교관 호칭(UI 문구) */
export const JOB_HERO_NAME: Readonly<Record<JobHeroId, string>> = { kael: '카엘', lyra: '라이라' };
export const JOB_TRAINER_TITLE: Readonly<Record<JobHeroId, string>> = { kael: '검술 교관 세르', lyra: '교관 세르 · 궁술 지도' };

/** 관문 하나(시련 판과 같은 판정 필드: kind·target) */
export interface JobGateDef {
  readonly id: string;
  readonly hero: JobHeroId;
  readonly tier: JobTier;
  /** 그 단계 안 순서(1..3). 앞 관문을 넘어야 다음이 열린다 */
  readonly index: number;
  readonly stage: AnyStageId;
  readonly region: RegionNo;
  readonly kind: TrialKind;
  /** combo = 최대 콤보, guard = 판 중 최저 HP %, perfect = 별 수(3) */
  readonly target: number;
  readonly name: string;
  /** 교관 대사(관문 소개) */
  readonly line: string;
  readonly hint: string;
  readonly reward: TrialReward;
}

/**
 * 관문 목표의 봇 측정값(최대 콤보, 시드 3개 중앙값). tier1 = 노말 지역2 판(qa/3month/c7/trialbot.json r2 4·7번), tier2 = 노말 지역3 판(qa/3month/c10-5/jobbot.json r3-s04).
 * 연격 목표 = 봇 × 0.5(시련과 같은 비율, 10 단위 내림). 측정을 다시 하면 이 표만 바꾼다
 */
// tier2 = 161(r3-s04 봇 3시드 161/156/167, Lv40~42·지역3 rare +12). tier1은 c7 표 97을 유지한다 — 이번 jobbot r2-s04는 Lv30~32·rare +9로
// 더 빨리 잡아 73(58~100)이 나왔고, 목표 40은 두 값 모두에서 봇이 넘는다(jobbot.json)
// 라이라 = qa/3month/c10-6/lyra-jobbot.json r2-s04·r3-s04(봇 3시드 중앙값, 같은 장비 단계 0)
// 라이라는 화살 다단(3연사·관통)이 콤보를 빨리 쌓아 카엘보다 높다: r2-s04 134(근접 Pilot 3시드 134/163/121), r3-s04 258(258/281/248) → 목표 60·120
export const JOB_GATE_BOT: Readonly<Record<JobHeroId, Readonly<Record<JobTier, number>>>> = { kael: { 1: 97, 2: 161 }, lyra: { 1: 134, 2: 258 } };
const down10 = (n: number) => Math.max(10, Math.floor(n / 10) * 10);
/** 인내 관문 문턱(시련 노말과 같은 70%)·완벽한 승리 별 수 */
export const JOB_TUNE = { comboShare: 0.5, guard: 70, perfectStars: 3 } as const;
/**
 * 영웅별 관문 조정(없으면 JOB_TUNE). 라이라 = 원거리 봇 재측정 qa/3month/c10-6/lyra-jobbot-ranged.json(시드 3, 장비 단계 0):
 * - 인내 70%: 1-2 1/3·2-2 0/3, 실패는 사망이 아니라 한두 대에 65.8~69.3%로 내려간 순간(HP가 낮은 궁수는 마진이 없다) → 지옥 시련 문턱과 같은 60%.
 * - 교관의 시험 II(r3-s10) 별 3: 3/3 클리어인데 별 1·1·2(판 345~383초, 끝 HP 20~60%) → 별 2개(시간 또는 체력 중 하나). I은 2/3이라 별 3 유지.
 * - 영웅별 무기 칸(lyra-weapon-slot, 2026-11-02) 재측정(활 = hw.lyra, qa/3month/c10-6/lyra-jobbot-ranged-bow-slot*.json): 6관문 결과가 PR #347 활 측정과 같다.
 *   인내 II(r3-s07) 장비 단계 0은 0/3(사망 없이 58.0~59.2%에서 실패). 문턱을 55%·50%로 낮춰 다시 재도 0/3(53.8~54·46.1~49.9%) —
 *   같은 교전(판 65~76초)에서 HP가 계속 깎이는 봇 동선 문제라 문턱 마진이 아니다(단계 1 장비는 3판 중 2판 통과, 72.1·63.3%). 문턱은 60% 그대로 두고 사람 실입력 표본으로 다시 본다.
 *   교관의 시험 II는 활 장착 시 별 2·2·3으로 3/3이라 별 2 유지.
 * 카엘은 근접 봇이 6관문 모두 3/3(qa/3month/c10-5/jobbot.json)이라 그대로.
 */
export const JOB_TUNE_HERO: Readonly<Partial<Record<JobHeroId, { readonly guard?: number; readonly perfectStars?: Partial<Record<JobTier, number>> }>>> = {
  lyra: { guard: 60, perfectStars: { 2: 2 } },
};
export const jobGuardOf = (hero: JobHeroId): number => JOB_TUNE_HERO[hero]?.guard ?? JOB_TUNE.guard;
export const jobPerfectStarsOf = (hero: JobHeroId, tier: JobTier): number => JOB_TUNE_HERO[hero]?.perfectStars?.[tier] ?? JOB_TUNE.perfectStars;

/** 관문 보상: 그 지역 노말 시련 하나와 같은 골드·강화석(지역2 1100·지역3 1100, 강화석 2) */
function rewardOf(region: RegionNo): TrialReward {
  return { gold: region === 1 ? 500 : 1100, stones: 2, tier: stoneTierForIlvl(REGIONS_90D[region - 1].levels[0] + DIFFICULTY_ILVL_ADD.normal) };
}

/** 관문 교관 대사(영웅별). combo = 연격 목표, boss = 지역 마지막 보스 이름 */
const GATE_LINES: Readonly<Record<JobHeroId, (tier: JobTier, combo: number, guard: number, boss: string) => readonly [string, string, string]>> = {
  kael: (tier, combo, guard, boss) => [
    tier === 1 ? '검은 끊기지 않을 때 무거워진다. 한 판에 ' + combo + '번을 이어 베어 봐라.' : '전직 검사라면 ' + combo + '연격쯤은 숨 쉬듯 이어야 한다.',
    tier === 1 ? '맞지 않는 것도 실력이다. 끝까지 HP ' + guard + '% 밑으로 떨어지지 마라.' : '더 깊은 곳의 적은 더 아프다. 그래도 ' + guard + '%를 지켜라.',
    tier === 1 ? boss + '을(를) 별 셋으로 쓰러뜨리면 네 갈래를 정해 주마.' : boss + '을(를) 완벽하게 꺾으면 두 번째 각인을 새겨 주마.',
  ],
  lyra: (tier, combo, guard, boss) => [
    tier === 1 ? '시위를 놓는 손이 쉬면 화살도 멈춘다. 한 판에 ' + combo + '발을 끊김 없이 꽂아 봐라.' : '전직 궁수라면 ' + combo + '연사쯤은 숨 쉬듯 이어야 한다.',
    tier === 1 ? '궁수는 맞는 순간 진다. 거리를 지켜서 끝까지 HP ' + guard + '% 밑으로 떨어지지 마라.' : '깊은 곳의 적은 더 빨리 붙는다. 물러서며 쏘고 ' + guard + '%를 지켜라.',
    tier === 1 ? boss + '을(를) 별 셋으로 쓰러뜨리면 네 활이 갈 길을 정해 주마.' : boss + '을(를) 별 둘 이상으로 꺾으면 활에 두 번째 각인을 새겨 주마.',
  ],
};

function gates(hero: JobHeroId, tier: JobTier): JobGateDef[] {
  const region: RegionNo = tier === 1 ? 2 : 3;
  const reg = REGIONS_90D[region - 1];
  const st = (i: number) => `r${region}-s${String(i).padStart(2, '0')}` as AnyStageId;
  const combo = down10(JOB_GATE_BOT[hero][tier] * JOB_TUNE.comboShare);
  const roman = tier === 1 ? 'I' : 'II';
  const guard = jobGuardOf(hero), stars = jobPerfectStarsOf(hero, tier);
  const lines = GATE_LINES[hero](tier, combo, guard, reg.finalBoss);
  const mk = (index: number, stage: number, kind: TrialKind, target: number, title: string, line: string, hint: string): JobGateDef =>
    ({ id: `job-${hero}-${tier}-${index}`, hero, tier, index, stage: st(stage), region, kind, target, name: `전직 ${roman} · ${title}`, line, hint, reward: rewardOf(region) });
  return [
    mk(1, 4, 'combo', combo, hero === 'lyra' ? '연사의 관문' : '연격의 관문', lines[0],
      `한 판 최대 콤보 ${combo} 이상 · 콤보는 2.5초 안에 다음 타격을 이어야 유지돼요`),
    mk(2, 7, 'guard', guard, '인내의 관문', lines[1],
      `판 내내 HP ${guard}% 이상을 지켜요 · 한 번이라도 떨어지면 관문 실패`),
    mk(3, 10, 'perfect', stars, '교관의 시험 · ' + reg.finalBoss, lines[2],
      stars >= 3 ? '별 3개: 5분 안에, HP 50% 이상으로 보스를 쓰러뜨려요' : `별 ${stars}개 이상: 클리어 + 5분 안 또는 HP 50% 이상 중 하나`),
  ];
}

export const JOB_GATES: readonly JobGateDef[] = JOB_HEROES.flatMap((h) => [...gates(h, 1), ...gates(h, 2)]);
const BY_ID = new Map(JOB_GATES.map((g) => [g.id, g]));
export function jobGateDef(id: unknown): JobGateDef | null { return typeof id === 'string' ? BY_ID.get(id) ?? null : null; }
export function jobGatesOf(hero: JobHeroId, tier: JobTier): readonly JobGateDef[] { return JOB_GATES.filter((g) => g.hero === hero && g.tier === tier); }
/** 관문 결과 이벤트 번호(Ev.TrialEnter b·Ev.TrialResult c): 시련 번호와 겹치지 않게 1000부터 */
export const JOB_EVENT_BASE = 1000;

// ── 고정 id ─────────────────────────────────────
export const jobGateRewardId = (id: string): string => 'jobgate:' + id;
export const jobPickId = (hero: JobHeroId, branch: JobBranch): string => `job:${hero}:1:${branch === 0 ? 'a' : 'b'}`;
export const jobAdvanceId = (hero: JobHeroId): string => `job:${hero}:2`;

/** 저장소에서 읽은 전직 상태(tier 0 = 전직 전) */
export interface JobState { readonly tier: 0 | JobTier; readonly branch: JobBranch }
/** 영수증 존재 여부로 상태를 만든다. II 영수증은 I 갈래가 있을 때만 인정한다(순서 어긋난 기록 방어) */
export function jobState(hero: JobHeroId, has: (id: string) => boolean): JobState {
  const a = has(jobPickId(hero, 0)), b = has(jobPickId(hero, 1));
  if (!a && !b) return { tier: 0, branch: 0 };
  return { tier: has(jobAdvanceId(hero)) ? 2 : 1, branch: a ? 0 : 1 };
}

/** 전직 스킬 번호(영웅 스킬 표 번호 = 전직 I·II): 카엘 9·10, 라이라 12·13(라이라 1..11은 이미 레벨 스킬) */
export const JOB_SKILL_ID: Readonly<Record<JobHeroId, Readonly<Record<JobTier, number>>>> = { kael: { 1: 9, 2: 10 }, lyra: { 1: 12, 2: 13 } };
/** 스킬 번호가 그 영웅의 전직 스킬이면 단계(1·2), 아니면 0 */
export function jobTierOfSkill(hero: unknown, skillId: number): 0 | JobTier {
  if (!isJobHero(hero)) return 0;
  const t = JOB_SKILL_ID[hero];
  return skillId === t[1] ? 1 : skillId === t[2] ? 2 : 0;
}
/** 전직 상태 → 배운 전직 스킬 비트(growthMods·learnedSkills의 kept 인자에 OR한다). 비트 i = 스킬 i+1 */
export function jobSkillMask(hero: JobHeroId, s: JobState): number {
  const t = JOB_SKILL_ID[hero];
  return (s.tier >= 1 ? 1 << (t[1] - 1) : 0) | (s.tier >= 2 ? 1 << (t[2] - 1) : 0);
}

/** 관문을 열 수 있는가: 레벨·앞 관문 통과·(II는) 전직 I 완료 */
export function jobGateOpen(g: JobGateDef, level: number, state: JobState, passed: (id: string) => boolean): boolean {
  if (level < JOB_LEVEL[g.tier]) return false;
  if (g.tier === 2 && state.tier < 1) return false;
  if (g.tier === 1 && state.tier >= 1) return true; // 전직 뒤에도 다시 도전은 된다(기록만)
  for (const p of jobGatesOf(g.hero, g.tier)) { if (p.index >= g.index) break; if (!passed(p.id)) return false; }
  return true;
}
/** 그 단계 관문을 모두 넘었는가 */
export function jobTierPassed(hero: JobHeroId, tier: JobTier, passed: (id: string) => boolean): boolean {
  return jobGatesOf(hero, tier).every((g) => passed(g.id));
}

/** 퀘스트 창·교관 창이 읽는 전직 상태(값이 바뀔 때만 rev가 오른다) */
export interface JobView {
  /** 전직 퀘스트가 있는 영웅인가(카엘·라이라) */
  readonly available: boolean;
  /** 지금 영웅(전직 영웅이 아니면 null) — 갈래 이름·관문 목록·교관 대사를 고른다 */
  readonly hero: JobHeroId | null;
  /** 0 전직 전 · 1 전직 I · 2 전직 II */
  readonly tier: number;
  /** 갈래 0·1(카엘 블러드 버서커·소드 마스터, 라이라 윈드 스나이퍼·트랩 레인저. tier 0이면 의미 없음) */
  readonly branch: number;
  /** 통과한 관문 id */
  readonly passed: readonly string[];
  /** 지금 들어갈 수 있는 관문 id */
  readonly open: readonly string[];
  /** 관문을 다 넘어 교관 앞 결정만 남은 단계(1 갈래 선택·2 승급), 없으면 0 */
  readonly ready: number;
  readonly rev: number;
}
